import { SQL_RESERVED_WORDS } from './query';
import type {
    VantageDataBlock,
    VantageDisplayMode,
    VantageDynamicVariable,
    VantageQueryResult,
    VantageVisualizationType,
} from '../../types';

const DATA_BLOCK_REFERENCE_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

export const getReservedDataBlockReferenceNames = (
    variables: VantageDynamicVariable[],
    dataBlocks: VantageDataBlock[],
    currentDataBlockId?: string,
) => {
    const reserved = new Set(SQL_RESERVED_WORDS.map((word) => word.toLowerCase()));

    variables.forEach((variable) => {
        reserved.add(variable.key.toLowerCase());
        reserved.add(variable.key.split('.')[0].toLowerCase());
    });

    dataBlocks.forEach((dataBlock) => {
        if (dataBlock.id !== currentDataBlockId) {
            reserved.add(dataBlock.referenceName.toLowerCase());
        }
    });

    return reserved;
};

export const getDataBlockReferenceNameError = (
    referenceName: string,
    variables: VantageDynamicVariable[],
    dataBlocks: VantageDataBlock[],
    currentDataBlockId?: string,
) => {
    const trimmedReferenceName = referenceName.trim();

    if (!trimmedReferenceName) {return 'Reference name is required.';}

    if (!DATA_BLOCK_REFERENCE_PATTERN.test(trimmedReferenceName)) {
        return 'Use letters, numbers, and underscores. Start with a letter or underscore.';
    }

    if (
        getReservedDataBlockReferenceNames(variables, dataBlocks, currentDataBlockId).has(
            trimmedReferenceName.toLowerCase(),
        )
    ) {
        return 'Reference name conflicts with a reserved keyword or dynamic variable.';
    }

    return undefined;
};

export const getNextDataBlockReferenceName = (
    variables: VantageDynamicVariable[],
    dataBlocks: VantageDataBlock[],
) => {
    for (let index = 1; index <= dataBlocks.length + 100; index += 1) {
        const candidate = `dataBlock${index}`;

        if (!getDataBlockReferenceNameError(candidate, variables, dataBlocks)) {
            return candidate;
        }
    }

    return `dataBlock${Date.now()}`;
};

export const createVantageDataBlock = ({
    dataBlocks,
    displayMode,
    limit,
    result,
    sql,
    variables,
    visualization,
}: {
    dataBlocks: VantageDataBlock[];
    displayMode: VantageDisplayMode;
    limit: number;
    result: VantageQueryResult;
    sql: string;
    variables: VantageDynamicVariable[];
    visualization: VantageVisualizationType;
}): VantageDataBlock => {
    const now = new Date().toISOString();

    return {
        id: `datablock-${Date.now()}`,
        referenceName: getNextDataBlockReferenceName(variables, dataBlocks),
        sql,
        visualization,
        displayMode,
        limit,
        columns: result.columns,
        rowCount: result.rowCount,
        runtimeMs: result.runtimeMs,
        result,
        createdAt: now,
        updatedAt: now,
    };
};
